/**
 * An inspectable search record, and the deterministic coverage estimate over it.
 *
 * Search transports stop at this file's boundary. A provider adapter may turn an
 * API response, an index page, or a local corpus scan into `CoverageRoundDraft`,
 * but nothing below performs retrieval and nothing asks a model what the search
 * covered. The trace is append-only input; the certificate is arithmetic over it.
 *
 * Coverage is deliberately an INTERVAL and deliberately an ESTIMATE. The lower
 * edge counts only admitted identities reached at full text against every known
 * and estimated gap. The upper edge gives every observed identity the benefit of
 * the doubt, but still reserves an unseen tail for every searched or declared-
 * but-unsearched part of the protocol. Consequently neither edge can reach one,
 * and `complete` is a literal `false` in every certificate.
 */

export const COVERAGE_CERTIFICATE_KIND = 'pathways-search-coverage-certificate'
export const COVERAGE_CERTIFICATE_V = 1

/** Stable names carried by the certificate, so its arithmetic can be re-run. */
export const COVERAGE_RULE = {
  admission: 'coverage.admission-as-recorded.v1',
  dedupe: 'coverage.identity-first-seen.v1',
  marginalYield: 'coverage.marginal-unique-yield.v1',
  boundaries: 'coverage.search-and-corpus-boundaries.v1',
  interval: 'coverage.estimated-interval-with-unseen-tail.v1',
  absence: 'coverage.absence-remains-weak.v1',
} as const

export type CoverageRuleName = (typeof COVERAGE_RULE)[keyof typeof COVERAGE_RULE]

/** How far the retrieval path actually reached for this particular result. */
export type ResultReach =
  'full-text' | 'abstract-only' | 'snippet' | 'blocked' | 'unreadable' | 'unsearched'

export interface QueryFamily<T = unknown> {
  id: string
  label: string
  /** Transport- or domain-specific fields, preserved without being interpreted. */
  meta?: T
}

export interface RetrievalSource<T = unknown> {
  id: string
  label: string
  /** Open web, scholarly index, local corpus, connector, or any future class. */
  kind?: string
  /** Transport- or domain-specific fields, preserved without being interpreted. */
  meta?: T
}

/** One query family that the protocol requires against one retrieval source. */
export interface RequiredCoverageCombination {
  queryFamilyId: string
  retrievalSourceId: string
}

/** The scope declared before or during a search. Unused entries become blind spots. */
export interface CoverageProtocol<TFamily = unknown, TSource = unknown> {
  id: string
  version: string
  queryFamilies: readonly QueryFamily<TFamily>[]
  retrievalSources: readonly RetrievalSource<TSource>[]
  /**
   * Required family/source pairs, when the protocol makes a matrix claim.
   *
   * Optional for stored v1 traces: absence means the older protocol declared the
   * two axes independently and made no claim about their cross-product. An empty
   * array is the explicit equivalent. Adding this field therefore cannot turn a
   * previously accepted trace into a new blind spot on reload.
   */
  requiredCombinations?: readonly RequiredCoverageCombination[]
}

export interface CoverageQuery {
  /** Identity of this issued query, not merely its text. Retries get new ids. */
  id: string
  text: string
  familyId: string
}

/** The versions and date against which a round's result counts were true. */
export interface CoverageBoundary {
  searchedAt: number
  searchVersion: string
  /** Null is explicit: this retrieval surface exposed no corpus version. */
  corpusVersion: string | null
}

export type RoundCompletion =
  | { kind: 'complete' }
  | { kind: 'partial'; code: string; reason: string }
  | { kind: 'failed'; code: string; reason: string }

/** Admission is supplied by the caller, with the exact named rule and reason. */
export type AdmissionDecision =
  | { kind: 'admitted'; rule: string; reason?: string }
  | { kind: 'rejected'; rule: string; code: string; reason: string }

export interface CoverageResult<T = unknown> {
  /** Identity of this row within its round. */
  id: string
  /** Stable work/result identity used for cross-query and cross-source dedupe. */
  identity: string
  reach: ResultReach
  admission: AdmissionDecision
  /** Provider payload or domain metadata, retained but never read by this module. */
  meta?: T
}

export interface CoverageRound<T = unknown> {
  id: string
  /** Landing order in the append-only trace. Assigned by `appendCoverageRound`. */
  order: number
  query: CoverageQuery
  retrievalSourceId: string
  boundary: CoverageBoundary
  completion: RoundCompletion
  /** Total the source reported, or null when it exposed no count. */
  reportedResultCount: number | null
  results: readonly CoverageResult<T>[]
}

export type CoverageRoundDraft<T = unknown> = Omit<CoverageRound<T>, 'order'>

export interface CoverageTrace<TFamily = unknown, TSource = unknown, TResult = unknown> {
  protocol: CoverageProtocol<TFamily, TSource>
  rounds: readonly CoverageRound<TResult>[]
}

export interface ResultRef {
  roundId: string
  resultId: string
}

export type DedupeDecision =
  | {
      kind: 'unique'
      rule: typeof COVERAGE_RULE.dedupe
      reason: 'First occurrence of this result identity.'
    }
  | {
      kind: 'duplicate'
      rule: typeof COVERAGE_RULE.dedupe
      duplicateOf: ResultRef
      reason: 'This result identity appeared earlier in the append-only trace.'
    }

/** One result occurrence, retaining admission and the independently computed dedupe. */
export interface CoverageResultReceipt {
  roundId: string
  roundOrder: number
  queryId: string
  queryFamilyId: string
  retrievalSourceId: string
  resultId: string
  identity: string
  reach: ResultReach
  admission: AdmissionDecision
  dedupe: DedupeDecision
}

export interface CoverageRejection {
  ref: ResultRef
  identity: string
  rule: string
  code: string
  reason: string
}

export interface CoverageDuplicate {
  ref: ResultRef
  identity: string
  duplicateOf: ResultRef
  rule: typeof COVERAGE_RULE.dedupe
  reason: DedupeDecision['reason']
}

export interface CoverageIdentity {
  identity: string
  firstSeen: ResultRef
  occurrences: readonly ResultRef[]
  strongestReach: ResultReach
  admitted: boolean
}

export interface RoundYield {
  roundId: string
  order: number
  queryFamilyId: string
  retrievalSourceId: string
  reportedResults: number | null
  resultRecords: number
  unrecordedReportedResults: number
  marginalUnique: number
  cumulativeUnique: number
  newlyAdmittedUnique: number
  cumulativeAdmittedUnique: number
  duplicateRecords: number
}

/** One round projected onto a caller-supplied, append-ordered trace campaign. */
export interface CoverageCampaignRoundYield extends RoundYield {
  campaignOrder: number
  traceOrder: number
  protocolId: string
  queryId: string
  queryText: string
  searchedAt: number
  completion: RoundCompletion['kind']
}

/**
 * Cross-trace marginal yield, deliberately not a coverage certificate.
 *
 * Each trace retains its own protocol, boundary, diagnostics, and absence claim.
 * This view only answers what each later recorded round added to identities seen
 * in earlier supplied traces; it does not manufacture one shared search scope.
 */
export interface CoverageCampaignYield {
  kind: 'recorded-campaign-yield'
  unifiedCertificate: false
  traces: number
  rounds: readonly CoverageCampaignRoundYield[]
  uniqueResults: number
  admittedUniqueResults: number
  note: string
}

export interface VersionBoundary {
  retrievalSourceId: string
  searchVersion: string
  corpusVersion: string | null
  firstRoundOrder: number
  lastRoundOrder: number
  roundIds: readonly string[]
  searchedAtMin: number
  searchedAtMax: number
}

export type CoverageDiagnosticKind =
  | 'no-search'
  | 'unsearched-family'
  | 'unsearched-source'
  | 'unsearched-combination'
  | 'failed-round'
  | 'partial-round'
  | 'unknown-result-count'
  | 'unrecorded-results'
  | 'limited-reach'
  | 'rejected-only'
  | 'version-boundary'
  | 'residual-unseen-tail'

export interface CoverageDiagnostic {
  id: string
  kind: CoverageDiagnosticKind
  rule: CoverageRuleName
  message: string
  roundIds: readonly string[]
  resultRefs: readonly ResultRef[]
  /** Every diagnostic here is an exact reason a negative search remains weak. */
  weakensAbsence: true
}

export interface CoverageInterval {
  kind: 'estimate'
  label: 'Estimated coverage interval'
  lower: number
  upper: number
  /** Literal, so a consumer cannot accidentally promote this into completeness. */
  complete: false
  inputs: {
    observedUnique: number
    fullTextAdmittedUnique: number
    residualUnseenEstimate: number
    unresolvedEstimate: number
    lowerScopeEstimate: number
    upperScopeEstimate: number
  }
  note: string
}

export interface CoverageCounts {
  rounds: number
  plannedQueryFamilies: number
  searchedQueryFamilies: number
  plannedRetrievalSources: number
  searchedRetrievalSources: number
  reportedResults: number
  roundsWithoutReportedCount: number
  resultRecords: number
  uniqueResults: number
  duplicateRecords: number
  admittedUniqueResults: number
  rejectionRecords: number
  fullTextAdmittedUniqueResults: number
  reachRecords: Record<ResultReach, number>
}

export type RuleFact = string | number | boolean | null

export interface CoverageRuleReceipt {
  rule: CoverageRuleName
  outcome: string
  facts: Readonly<Record<string, RuleFact>>
}

export interface AbsenceAssessment {
  status: 'weak'
  supportsAbsence: false
  reasonIds: readonly string[]
  explanation: string
}

export interface CoverageCertificate<TFamily = unknown, TSource = unknown, TResult = unknown> {
  kind: typeof COVERAGE_CERTIFICATE_KIND
  v: typeof COVERAGE_CERTIFICATE_V
  protocol: CoverageProtocol<TFamily, TSource>
  rounds: readonly CoverageRound<TResult>[]
  searchWindow: { from: number; to: number } | null
  boundaries: readonly VersionBoundary[]
  results: readonly CoverageResultReceipt[]
  identities: readonly CoverageIdentity[]
  rejections: readonly CoverageRejection[]
  duplicates: readonly CoverageDuplicate[]
  yield: readonly RoundYield[]
  counts: CoverageCounts
  interval: CoverageInterval
  diagnostics: readonly CoverageDiagnostic[]
  absence: AbsenceAssessment
  rules: readonly CoverageRuleReceipt[]
}

const REACH: readonly ResultReach[] = [
  'full-text',
  'abstract-only',
  'snippet',
  'blocked',
  'unreadable',
  'unsearched',
]

const REACH_RANK: Record<ResultReach, number> = {
  'full-text': 5,
  'abstract-only': 4,
  snippet: 3,
  blocked: 2,
  unreadable: 1,
  unsearched: 0,
}

const nonblank = (s: string): boolean => s.trim().length > 0

function assertNonblank(value: string, field: string): void {
  if (!nonblank(value)) throw new Error(`${field} must be non-empty.`)
}

function assertDistinct<T>(rows: readonly T[], id: (row: T) => string, field: string): void {
  const seen = new Set<string>()
  for (const row of rows) {
    const value = id(row)
    assertNonblank(value, field)
    if (seen.has(value)) throw new Error(`${field} must be unique: ${value}`)
    seen.add(value)
  }
}

function validateProtocol(protocol: CoverageProtocol<unknown, unknown>): void {
  assertNonblank(protocol.id, 'protocol.id')
  assertNonblank(protocol.version, 'protocol.version')
  assertDistinct(protocol.queryFamilies, (row) => row.id, 'queryFamilies.id')
  assertDistinct(protocol.retrievalSources, (row) => row.id, 'retrievalSources.id')
  for (const family of protocol.queryFamilies) assertNonblank(family.label, 'queryFamilies.label')
  for (const source of protocol.retrievalSources)
    assertNonblank(source.label, 'retrievalSources.label')

  const familyIds = new Set(protocol.queryFamilies.map((row) => row.id))
  const sourceIds = new Set(protocol.retrievalSources.map((row) => row.id))
  const combinations = protocol.requiredCombinations ?? []
  assertDistinct(
    combinations,
    (row) => JSON.stringify([row.queryFamilyId, row.retrievalSourceId]),
    'requiredCombinations',
  )
  for (const combination of combinations) {
    assertNonblank(combination.queryFamilyId, 'requiredCombinations.queryFamilyId')
    assertNonblank(combination.retrievalSourceId, 'requiredCombinations.retrievalSourceId')
    if (!familyIds.has(combination.queryFamilyId)) {
      throw new Error(
        `requiredCombinations contains an unknown query family: ${combination.queryFamilyId}`,
      )
    }
    if (!sourceIds.has(combination.retrievalSourceId)) {
      throw new Error(
        `requiredCombinations contains an unknown retrieval source: ${combination.retrievalSourceId}`,
      )
    }
  }
}

function validateRound(
  protocol: CoverageProtocol<unknown, unknown>,
  round: CoverageRoundDraft<unknown>,
): void {
  assertNonblank(round.id, 'round.id')
  assertNonblank(round.query.id, 'round.query.id')
  assertNonblank(round.query.text, 'round.query.text')
  if (!protocol.queryFamilies.some((row) => row.id === round.query.familyId)) {
    throw new Error(`Unknown query family: ${round.query.familyId}`)
  }
  if (!protocol.retrievalSources.some((row) => row.id === round.retrievalSourceId)) {
    throw new Error(`Unknown retrieval source: ${round.retrievalSourceId}`)
  }
  if (!Number.isFinite(round.boundary.searchedAt) || round.boundary.searchedAt < 0) {
    throw new Error('round.boundary.searchedAt must be a non-negative finite number.')
  }
  assertNonblank(round.boundary.searchVersion, 'round.boundary.searchVersion')
  if (round.boundary.corpusVersion !== null) {
    assertNonblank(round.boundary.corpusVersion, 'round.boundary.corpusVersion')
  }
  if (
    round.reportedResultCount !== null &&
    (!Number.isInteger(round.reportedResultCount) ||
      round.reportedResultCount < 0 ||
      round.reportedResultCount < round.results.length)
  ) {
    throw new Error(
      'round.reportedResultCount must be null or an integer covering every result row.',
    )
  }
  if (round.completion.kind !== 'complete') {
    assertNonblank(round.completion.code, 'round.completion.code')
    assertNonblank(round.completion.reason, 'round.completion.reason')
  }
  assertDistinct(round.results, (row) => row.id, 'round.results.id')
  for (const result of round.results) {
    assertNonblank(result.identity, 'round.results.identity')
    assertNonblank(result.admission.rule, 'round.results.admission.rule')
    if (result.admission.kind === 'rejected') {
      assertNonblank(result.admission.code, 'round.results.admission.code')
      assertNonblank(result.admission.reason, 'round.results.admission.reason')
    }
  }
}

function validateTrace(trace: CoverageTrace<unknown, unknown, unknown>): void {
  validateProtocol(trace.protocol)
  assertDistinct(trace.rounds, (row) => row.id, 'round.id')
  assertDistinct(trace.rounds, (row) => row.query.id, 'round.query.id')
  for (let i = 0; i < trace.rounds.length; i++) {
    const round = trace.rounds[i]!
    if (round.order !== i + 1) throw new Error(`round.order must be append order: ${i + 1}`)
    const { order: _order, ...draft } = round
    validateRound(trace.protocol, draft)
  }
}

/** Start an empty trace. Protocol scope is retained even before the first query. */
export function createCoverageTrace<TFamily = unknown, TSource = unknown, TResult = unknown>(
  protocol: CoverageProtocol<TFamily, TSource>,
): CoverageTrace<TFamily, TSource, TResult> {
  validateProtocol(protocol)
  return {
    protocol: {
      ...protocol,
      queryFamilies: protocol.queryFamilies.map((row) => ({ ...row })),
      retrievalSources: protocol.retrievalSources.map((row) => ({ ...row })),
      ...(protocol.requiredCombinations
        ? { requiredCombinations: protocol.requiredCombinations.map((row) => ({ ...row })) }
        : {}),
    },
    rounds: [],
  }
}

/**
 * Append one landed round without changing any prior view of the trace.
 *
 * Order comes from landing, not from `searchedAt`: an offline source may be
 * imported later with an older date, and chronology must not rewrite append order.
 */
export function appendCoverageRound<TFamily, TSource, TResult>(
  trace: CoverageTrace<TFamily, TSource, TResult>,
  draft: CoverageRoundDraft<TResult>,
): CoverageTrace<TFamily, TSource, TResult> {
  validateTrace(trace)
  validateRound(trace.protocol, draft)
  if (trace.rounds.some((round) => round.id === draft.id)) {
    throw new Error(`round.id must be unique: ${draft.id}`)
  }
  if (trace.rounds.some((round) => round.query.id === draft.query.id)) {
    throw new Error(`round.query.id must be unique: ${draft.query.id}`)
  }
  const round: CoverageRound<TResult> = {
    ...draft,
    order: trace.rounds.length + 1,
    query: { ...draft.query },
    boundary: { ...draft.boundary },
    completion: { ...draft.completion },
    results: draft.results.map((result) => ({
      ...result,
      admission: { ...result.admission },
    })),
  }
  return { protocol: trace.protocol, rounds: [...trace.rounds, round] }
}

const resultRef = (roundId: string, resultId: string): ResultRef => ({ roundId, resultId })

const roundEstimate = (n: number): number => Math.round(n * 1_000_000) / 1_000_000

/** Rounding must never turn 0.9999999 into the unsupported figure 1. */
const estimateUnit = (n: number): number => Math.min(0.999999, Math.max(0, roundEstimate(n)))

function boundaryRows(rounds: readonly CoverageRound<unknown>[]): VersionBoundary[] {
  const rows = new Map<string, VersionBoundary>()
  for (const round of rounds) {
    const key = JSON.stringify([
      round.retrievalSourceId,
      round.boundary.searchVersion,
      round.boundary.corpusVersion,
    ])
    const held = rows.get(key)
    if (held) {
      held.lastRoundOrder = round.order
      held.roundIds = [...held.roundIds, round.id]
      held.searchedAtMin = Math.min(held.searchedAtMin, round.boundary.searchedAt)
      held.searchedAtMax = Math.max(held.searchedAtMax, round.boundary.searchedAt)
    } else {
      rows.set(key, {
        retrievalSourceId: round.retrievalSourceId,
        searchVersion: round.boundary.searchVersion,
        corpusVersion: round.boundary.corpusVersion,
        firstRoundOrder: round.order,
        lastRoundOrder: round.order,
        roundIds: [round.id],
        searchedAtMin: round.boundary.searchedAt,
        searchedAtMax: round.boundary.searchedAt,
      })
    }
  }
  return [...rows.values()]
}

interface IdentityWork {
  identity: string
  firstSeen: ResultRef
  occurrences: ResultRef[]
  strongestReach: ResultReach
  admitted: boolean
  fullTextAdmitted: boolean
}

interface Projection {
  receipts: CoverageResultReceipt[]
  identities: IdentityWork[]
  rejections: CoverageRejection[]
  duplicates: CoverageDuplicate[]
  yields: RoundYield[]
  reachRecords: Record<ResultReach, number>
}

function project(rounds: readonly CoverageRound<unknown>[]): Projection {
  const first = new Map<string, ResultRef>()
  const identities = new Map<string, IdentityWork>()
  const admitted = new Set<string>()
  const receipts: CoverageResultReceipt[] = []
  const rejections: CoverageRejection[] = []
  const duplicates: CoverageDuplicate[] = []
  const yields: RoundYield[] = []
  const reachRecords: Record<ResultReach, number> = {
    'full-text': 0,
    'abstract-only': 0,
    snippet: 0,
    blocked: 0,
    unreadable: 0,
    unsearched: 0,
  }

  for (const round of rounds) {
    let marginalUnique = 0
    let newlyAdmittedUnique = 0
    let duplicateRecords = 0
    for (const result of round.results) {
      reachRecords[result.reach]++
      const ref = resultRef(round.id, result.id)
      const earlier = first.get(result.identity)
      const dedupe: DedupeDecision = earlier
        ? {
            kind: 'duplicate',
            rule: COVERAGE_RULE.dedupe,
            duplicateOf: earlier,
            reason: 'This result identity appeared earlier in the append-only trace.',
          }
        : {
            kind: 'unique',
            rule: COVERAGE_RULE.dedupe,
            reason: 'First occurrence of this result identity.',
          }

      if (earlier) {
        duplicateRecords++
        duplicates.push({
          ref,
          identity: result.identity,
          duplicateOf: earlier,
          rule: COVERAGE_RULE.dedupe,
          reason: dedupe.reason,
        })
      } else {
        first.set(result.identity, ref)
        marginalUnique++
        identities.set(result.identity, {
          identity: result.identity,
          firstSeen: ref,
          occurrences: [],
          strongestReach: result.reach,
          admitted: false,
          fullTextAdmitted: false,
        })
      }

      const identity = identities.get(result.identity)!
      identity.occurrences.push(ref)
      if (REACH_RANK[result.reach] > REACH_RANK[identity.strongestReach]) {
        identity.strongestReach = result.reach
      }
      if (result.admission.kind === 'admitted') {
        identity.admitted = true
        if (!admitted.has(result.identity)) {
          admitted.add(result.identity)
          newlyAdmittedUnique++
        }
        if (result.reach === 'full-text') identity.fullTextAdmitted = true
      } else {
        rejections.push({
          ref,
          identity: result.identity,
          rule: result.admission.rule,
          code: result.admission.code,
          reason: result.admission.reason,
        })
      }

      receipts.push({
        roundId: round.id,
        roundOrder: round.order,
        queryId: round.query.id,
        queryFamilyId: round.query.familyId,
        retrievalSourceId: round.retrievalSourceId,
        resultId: result.id,
        identity: result.identity,
        reach: result.reach,
        admission: result.admission,
        dedupe,
      })
    }

    yields.push({
      roundId: round.id,
      order: round.order,
      queryFamilyId: round.query.familyId,
      retrievalSourceId: round.retrievalSourceId,
      reportedResults: round.reportedResultCount,
      resultRecords: round.results.length,
      unrecordedReportedResults:
        round.reportedResultCount === null ? 0 : round.reportedResultCount - round.results.length,
      marginalUnique,
      cumulativeUnique: identities.size,
      newlyAdmittedUnique,
      cumulativeAdmittedUnique: admitted.size,
      duplicateRecords,
    })
  }

  return {
    receipts,
    identities: [...identities.values()],
    rejections,
    duplicates,
    yields,
    reachRecords,
  }
}

/**
 * Project cumulative marginal yield across separately certified stored traces.
 *
 * Input order is the campaign order. No timestamp sort is attempted: append
 * history, including a later import with an older search date, must not rewrite
 * what was known before each recorded check.
 */
export function coverageCampaignYield(
  traces: readonly CoverageTrace<unknown, unknown, unknown>[],
): CoverageCampaignYield {
  const seen = new Set<string>()
  const admitted = new Set<string>()
  const rounds: CoverageCampaignRoundYield[] = []

  for (const [traceIndex, trace] of traces.entries()) {
    validateTrace(trace)
    for (const round of trace.rounds) {
      let marginalUnique = 0
      let newlyAdmittedUnique = 0
      let duplicateRecords = 0
      for (const result of round.results) {
        if (seen.has(result.identity)) duplicateRecords++
        else {
          seen.add(result.identity)
          marginalUnique++
        }
        if (result.admission.kind === 'admitted' && !admitted.has(result.identity)) {
          admitted.add(result.identity)
          newlyAdmittedUnique++
        }
      }
      rounds.push({
        roundId: round.id,
        order: round.order,
        campaignOrder: rounds.length + 1,
        traceOrder: traceIndex + 1,
        protocolId: trace.protocol.id,
        queryId: round.query.id,
        queryText: round.query.text,
        queryFamilyId: round.query.familyId,
        retrievalSourceId: round.retrievalSourceId,
        searchedAt: round.boundary.searchedAt,
        completion: round.completion.kind,
        reportedResults: round.reportedResultCount,
        resultRecords: round.results.length,
        unrecordedReportedResults:
          round.reportedResultCount === null ? 0 : round.reportedResultCount - round.results.length,
        marginalUnique,
        cumulativeUnique: seen.size,
        newlyAdmittedUnique,
        cumulativeAdmittedUnique: admitted.size,
        duplicateRecords,
      })
    }
  }

  return {
    kind: 'recorded-campaign-yield',
    unifiedCertificate: false,
    traces: traces.length,
    rounds,
    uniqueResults: seen.size,
    admittedUniqueResults: admitted.size,
    note: 'Cumulative yield over the supplied append-ordered records; each trace keeps its own protocol and completeness limits.',
  }
}

function diagnostic(
  id: string,
  kind: CoverageDiagnosticKind,
  rule: CoverageRuleName,
  message: string,
  roundIds: readonly string[] = [],
  resultRefs: readonly ResultRef[] = [],
): CoverageDiagnostic {
  return { id, kind, rule, message, roundIds, resultRefs, weakensAbsence: true }
}

interface EstimateWork {
  residualUnseen: number
  unresolved: number
  diagnostics: CoverageDiagnostic[]
}

function estimateGaps<TFamily, TSource, TResult>(
  trace: CoverageTrace<TFamily, TSource, TResult>,
  projection: Projection,
  boundaries: readonly VersionBoundary[],
): EstimateWork {
  const diagnostics: CoverageDiagnostic[] = []
  const searchedFamilies = new Set(trace.rounds.map((round) => round.query.familyId))
  const searchedSources = new Set(trace.rounds.map((round) => round.retrievalSourceId))
  const searchedCombinations = new Set(
    trace.rounds.map((round) => JSON.stringify([round.query.familyId, round.retrievalSourceId])),
  )
  const successfulYields = projection.yields.filter(
    (row) => trace.rounds[row.order - 1]?.completion.kind !== 'failed',
  )
  const baseline = Math.max(
    1,
    Math.ceil(
      successfulYields.reduce((sum, row) => sum + row.marginalUnique, 0) /
        Math.max(1, successfulYields.length),
    ),
  )

  if (trace.rounds.length === 0) {
    diagnostics.push(
      diagnostic(
        'no-search',
        'no-search',
        COVERAGE_RULE.absence,
        'No search round was recorded, so there is no retrieval evidence behind an absence claim.',
      ),
    )
  }

  let residualUnseen = 0
  let unresolved = 0

  for (const family of trace.protocol.queryFamilies) {
    if (searchedFamilies.has(family.id)) continue
    residualUnseen += baseline
    unresolved += baseline
    diagnostics.push(
      diagnostic(
        `unsearched-family:${family.id}`,
        'unsearched-family',
        COVERAGE_RULE.absence,
        `Query family “${family.label}” (${family.id}) was declared but never searched.`,
      ),
    )
  }

  for (const source of trace.protocol.retrievalSources) {
    if (searchedSources.has(source.id)) continue
    residualUnseen += baseline
    unresolved += baseline
    diagnostics.push(
      diagnostic(
        `unsearched-source:${source.id}`,
        'unsearched-source',
        COVERAGE_RULE.absence,
        `Retrieval source “${source.label}” (${source.id}) was declared but never searched.`,
      ),
    )
  }

  const familyById = new Map(trace.protocol.queryFamilies.map((row) => [row.id, row]))
  const sourceById = new Map(trace.protocol.retrievalSources.map((row) => [row.id, row]))
  for (const combination of trace.protocol.requiredCombinations ?? []) {
    const key = JSON.stringify([combination.queryFamilyId, combination.retrievalSourceId])
    if (searchedCombinations.has(key)) continue
    residualUnseen += baseline
    unresolved += baseline
    const family = familyById.get(combination.queryFamilyId)!
    const source = sourceById.get(combination.retrievalSourceId)!
    diagnostics.push(
      diagnostic(
        `unsearched-combination:${encodeURIComponent(combination.queryFamilyId)}:${encodeURIComponent(combination.retrievalSourceId)}`,
        'unsearched-combination',
        COVERAGE_RULE.absence,
        `Required combination “${family.label}” (${family.id}) × “${source.label}” (${source.id}) was declared but never searched.`,
      ),
    )
  }

  const pairYields = new Map<string, RoundYield[]>()
  for (const row of projection.yields) {
    const key = JSON.stringify([row.queryFamilyId, row.retrievalSourceId])
    const held = pairYields.get(key)
    if (held) held.push(row)
    else pairYields.set(key, [row])
  }
  for (const rows of pairYields.values()) {
    const landed = rows.filter((row) => trace.rounds[row.order - 1]?.completion.kind !== 'failed')
    const last = landed.at(-1)
    residualUnseen += last ? Math.max(1, last.marginalUnique) : baseline
  }

  for (const round of trace.rounds) {
    const row = projection.yields[round.order - 1]!
    const refs = round.results.map((result) => resultRef(round.id, result.id))
    const missing = row.unrecordedReportedResults
    if (missing > 0) {
      unresolved += missing
      diagnostics.push(
        diagnostic(
          `unrecorded-results:${round.id}`,
          'unrecorded-results',
          COVERAGE_RULE.absence,
          `Round ${round.id} reported ${round.reportedResultCount} results but retained ${round.results.length}; ${missing} result${missing === 1 ? '' : 's'} cannot be inspected.`,
          [round.id],
        ),
      )
    }
    if (round.reportedResultCount === null) {
      const penalty = Math.max(1, row.marginalUnique)
      unresolved += penalty
      diagnostics.push(
        diagnostic(
          `unknown-result-count:${round.id}`,
          'unknown-result-count',
          COVERAGE_RULE.absence,
          `Round ${round.id} exposed no total result count, so the number of unretained results is unknown.`,
          [round.id],
        ),
      )
    }
    if (round.completion.kind === 'partial') {
      unresolved += Math.max(1, missing, Math.ceil(row.marginalUnique / 2))
      diagnostics.push(
        diagnostic(
          `partial-round:${round.id}`,
          'partial-round',
          COVERAGE_RULE.absence,
          `Round ${round.id} was partial (${round.completion.code}): ${round.completion.reason}`,
          [round.id],
          refs,
        ),
      )
    } else if (round.completion.kind === 'failed') {
      unresolved += baseline
      diagnostics.push(
        diagnostic(
          `failed-round:${round.id}`,
          'failed-round',
          COVERAGE_RULE.absence,
          `Round ${round.id} failed (${round.completion.code}): ${round.completion.reason}`,
          [round.id],
        ),
      )
    }
  }

  for (const reach of REACH.filter((value) => value !== 'full-text')) {
    const rows = projection.receipts.filter((row) => row.reach === reach)
    if (rows.length === 0) continue
    const labels = rows.map((row) => `${row.roundId}/${row.resultId}`).join(', ')
    const reading =
      reach === 'abstract-only'
        ? 'only an abstract was available'
        : reach === 'snippet'
          ? 'only a search snippet was available'
          : reach === 'blocked'
            ? 'retrieval was blocked'
            : reach === 'unreadable'
              ? 'the returned content could not be read'
              : 'the result was identified but never searched'
    diagnostics.push(
      diagnostic(
        `limited-reach:${reach}`,
        'limited-reach',
        COVERAGE_RULE.absence,
        `${rows.length} result${rows.length === 1 ? '' : 's'} had reach “${reach}” (${labels}); ${reading}.`,
        [...new Set(rows.map((row) => row.roundId))],
        rows.map((row) => resultRef(row.roundId, row.resultId)),
      ),
    )
  }

  if (projection.identities.length > 0 && !projection.identities.some((row) => row.admitted)) {
    diagnostics.push(
      diagnostic(
        'rejected-only',
        'rejected-only',
        COVERAGE_RULE.admission,
        `The search found ${projection.identities.length} unique result${projection.identities.length === 1 ? '' : 's'}, but none was admitted; rejection is not evidence that the material does not exist.`,
        trace.rounds.map((round) => round.id),
        projection.receipts.map((row) => resultRef(row.roundId, row.resultId)),
      ),
    )
  }

  const boundaryVersions = new Map<string, VersionBoundary[]>()
  for (const boundary of boundaries) {
    const held = boundaryVersions.get(boundary.retrievalSourceId)
    if (held) held.push(boundary)
    else boundaryVersions.set(boundary.retrievalSourceId, [boundary])
  }
  for (const [sourceId, rows] of boundaryVersions) {
    if (rows.length <= 1) continue
    const versions = rows
      .map((row) => `${row.searchVersion}/${row.corpusVersion ?? 'no-corpus-version'}`)
      .join(', ')
    diagnostics.push(
      diagnostic(
        `version-boundary:${sourceId}`,
        'version-boundary',
        COVERAGE_RULE.boundaries,
        `Retrieval source ${sourceId} crossed search/corpus version boundaries (${versions}); its rounds do not describe one frozen corpus.`,
        rows.flatMap((row) => row.roundIds),
      ),
    )
  }

  if (residualUnseen === 0) residualUnseen = 1
  diagnostics.push(
    diagnostic(
      'residual-unseen-tail',
      'residual-unseen-tail',
      COVERAGE_RULE.interval,
      `The estimated interval reserves ${residualUnseen} unseen result${residualUnseen === 1 ? '' : 's'}; a finite query trace cannot certify exhaustive coverage.`,
      trace.rounds.map((round) => round.id),
    ),
  )

  return { residualUnseen, unresolved, diagnostics }
}

/** Build the portable, deterministic projection of one append-only trace. */
export function coverageCertificate<TFamily = unknown, TSource = unknown, TResult = unknown>(
  trace: CoverageTrace<TFamily, TSource, TResult>,
): CoverageCertificate<TFamily, TSource, TResult> {
  validateTrace(trace)
  const projection = project(trace.rounds)
  const boundaries = boundaryRows(trace.rounds)
  const gaps = estimateGaps(trace, projection, boundaries)
  const observedUnique = projection.identities.length
  const fullTextAdmittedUnique = projection.identities.filter((row) => row.fullTextAdmitted).length
  const upperScope = observedUnique + gaps.residualUnseen
  const lowerScope = upperScope + gaps.unresolved
  const upper = observedUnique === 0 ? 0 : estimateUnit(observedUnique / upperScope)
  const lower = Math.min(
    upper,
    fullTextAdmittedUnique === 0 ? 0 : estimateUnit(fullTextAdmittedUnique / lowerScope),
  )
  const interval: CoverageInterval = {
    kind: 'estimate',
    label: 'Estimated coverage interval',
    lower,
    upper,
    complete: false,
    inputs: {
      observedUnique,
      fullTextAdmittedUnique,
      residualUnseenEstimate: gaps.residualUnseen,
      unresolvedEstimate: gaps.unresolved,
      lowerScopeEstimate: lowerScope,
      upperScopeEstimate: upperScope,
    },
    note: 'An arithmetic estimate over this recorded protocol, not a claim that the searchable universe is known or complete.',
  }

  const searchedFamilies = new Set(trace.rounds.map((round) => round.query.familyId))
  const searchedSources = new Set(trace.rounds.map((round) => round.retrievalSourceId))
  const admittedUnique = projection.identities.filter((row) => row.admitted).length
  const reportedResults = trace.rounds.reduce(
    (sum, round) => sum + (round.reportedResultCount ?? 0),
    0,
  )
  const counts: CoverageCounts = {
    rounds: trace.rounds.length,
    plannedQueryFamilies: trace.protocol.queryFamilies.length,
    searchedQueryFamilies: searchedFamilies.size,
    plannedRetrievalSources: trace.protocol.retrievalSources.length,
    searchedRetrievalSources: searchedSources.size,
    reportedResults,
    roundsWithoutReportedCount: trace.rounds.filter((round) => round.reportedResultCount === null)
      .length,
    resultRecords: projection.receipts.length,
    uniqueResults: observedUnique,
    duplicateRecords: projection.duplicates.length,
    admittedUniqueResults: admittedUnique,
    rejectionRecords: projection.rejections.length,
    fullTextAdmittedUniqueResults: fullTextAdmittedUnique,
    reachRecords: projection.reachRecords,
  }

  const absenceReasons = gaps.diagnostics.map((row) => row.id)
  const explanationLead =
    admittedUnique === 0
      ? 'No result was admitted, but that does not establish absence.'
      : 'This trace cannot establish that evidence outside its admitted results is absent.'
  const absence: AbsenceAssessment = {
    status: 'weak',
    supportsAbsence: false,
    reasonIds: absenceReasons,
    explanation: `${explanationLead} ${gaps.diagnostics.map((row) => row.message).join(' ')}`,
  }

  const rules: CoverageRuleReceipt[] = [
    {
      rule: COVERAGE_RULE.dedupe,
      outcome: `${projection.receipts.length} result records reduced to ${observedUnique} stable identities without deleting any occurrence.`,
      facts: {
        resultRecords: projection.receipts.length,
        uniqueResults: observedUnique,
        duplicateRecords: projection.duplicates.length,
      },
    },
    {
      rule: COVERAGE_RULE.admission,
      outcome:
        'Admission and rejection were copied from the generic result records; this module made no relevance judgment.',
      facts: {
        admittedUniqueResults: admittedUnique,
        rejectionRecords: projection.rejections.length,
      },
    },
    {
      rule: COVERAGE_RULE.marginalYield,
      outcome: 'Each round counted an identity only when it first appeared in append order.',
      facts: { rounds: trace.rounds.length, cumulativeUniqueResults: observedUnique },
    },
    {
      rule: COVERAGE_RULE.boundaries,
      outcome: 'Rounds were partitioned by retrieval source, search version, and corpus version.',
      facts: { boundaries: boundaries.length },
    },
    {
      rule: COVERAGE_RULE.interval,
      outcome:
        'The lower edge used full-text admitted identities and all uncertainty; the upper edge used all observed identities and retained an unseen tail.',
      facts: {
        lower,
        upper,
        complete: false,
        residualUnseenEstimate: gaps.residualUnseen,
        unresolvedEstimate: gaps.unresolved,
      },
    },
    {
      rule: COVERAGE_RULE.absence,
      outcome: 'Absence remained weak and every limiting condition was itemized.',
      facts: { supportsAbsence: false, diagnosticCount: gaps.diagnostics.length },
    },
  ]

  const dates = trace.rounds.map((round) => round.boundary.searchedAt)
  return {
    kind: COVERAGE_CERTIFICATE_KIND,
    v: COVERAGE_CERTIFICATE_V,
    protocol: trace.protocol,
    rounds: trace.rounds,
    searchWindow: dates.length ? { from: Math.min(...dates), to: Math.max(...dates) } : null,
    boundaries,
    results: projection.receipts,
    identities: projection.identities.map((row) => ({
      identity: row.identity,
      firstSeen: row.firstSeen,
      occurrences: row.occurrences,
      strongestReach: row.strongestReach,
      admitted: row.admitted,
    })),
    rejections: projection.rejections,
    duplicates: projection.duplicates,
    yield: projection.yields,
    counts,
    interval,
    diagnostics: gaps.diagnostics,
    absence,
    rules,
  }
}

/** Observed work only: no inferred search universe or calibrated recall. */
export interface RecordedSearchObservation {
  kind: 'recorded-search-v1'
  protocolId: string
  protocolVersion: string
  completedRounds: number
  attemptedRounds: number
  resultIdentities: number
  admittedIdentities: number
  fullTextAdmittedIdentities: number
  queriedFamilies: string[]
  reachedSources: string[]
  boundaries: readonly VersionBoundary[]
  gaps: Array<{ kind: CoverageDiagnosticKind; message: string; roundIds: readonly string[] }>
  supportsAbsence: false
  supportsNovelty: false
}

/**
 * Historical certificate arithmetic is left unchanged. This projection omits
 * the invented unseen denominator and reports exact work and declared limits.
 */
export function recordedSearchObservation(
  certificate: CoverageCertificate,
): RecordedSearchObservation {
  return {
    kind: 'recorded-search-v1',
    protocolId: certificate.protocol.id,
    protocolVersion: certificate.protocol.version,
    completedRounds: certificate.rounds.filter((round) => round.completion.kind === 'complete')
      .length,
    attemptedRounds: certificate.rounds.length,
    resultIdentities: certificate.counts.uniqueResults,
    admittedIdentities: certificate.counts.admittedUniqueResults,
    fullTextAdmittedIdentities: certificate.counts.fullTextAdmittedUniqueResults,
    queriedFamilies: [...new Set(certificate.rounds.map((round) => round.query.familyId))],
    reachedSources: [...new Set(certificate.rounds.map((round) => round.retrievalSourceId))],
    boundaries: certificate.boundaries,
    gaps: certificate.diagnostics
      .filter((gap) => gap.kind !== 'residual-unseen-tail' && gap.kind !== 'version-boundary')
      .map(({ kind, message, roundIds }) => ({ kind, message, roundIds })),
    supportsAbsence: false,
    supportsNovelty: false,
  }
}
